import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import { categories } from '@/mocks/categories';

const FORM_ENDPOINT = 'https://readdy.ai/api/form/db960egkk00l6eltrpeg';

const contacts = [
  { icon: 'ri-mail-line', label: '商务合作邮箱', value: 'business@douyin-demo.com' },
  { icon: 'ri-customer-service-2-line', label: '客服热线', value: '400-800-2026' },
  { icon: 'ri-map-pin-line', label: '内容运营中心', value: '北京市朝阳区创意园区 8 号楼' },
];

const stats = [
  { value: '32+', label: '精选演示视频' },
  { value: '8', label: '热门内容分类' },
  { value: '24h', label: '内容实时更新' },
  { value: '100%', label: '移动端适配' },
];

export default function AboutPage() {
  usePageMeta({
    title: '关于我们 - 抖音短视频内容分享平台',
    description:
      '抖音是面向年轻人的短视频内容分享与浏览平台，覆盖音乐、搞笑、生活、美食等多个热门领域，记录美好生活，发现精彩内容。',
    keywords: '抖音,关于我们,短视频平台,内容分享,抖音短视频',
  });

  const [toast, setToast] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [fieldError, setFieldError] = useState('');

  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(''), 2000);
    return () => window.clearTimeout(id);
  }, [toast]);

  const copyText = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setToast(`${label}已复制：${text}`);
    } catch {
      setToast(`${label}：${text}`);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // 反垃圾蜜罐校验
    const honey = String(data.get('website_alt') ?? '').trim();
    if (honey) {
      setStatus('success');
      form.reset();
      return;
    }

    const nickname = String(data.get('nickname') ?? '').trim();
    const email = String(data.get('email') ?? '').trim();
    const content = String(data.get('content') ?? '').trim();
    if (!nickname || !email || !content) {
      setFieldError('请把昵称、邮箱和反馈内容都填写完整');
      setStatus('error');
      return;
    }
    setFieldError('');
    setStatus('loading');
    setFormError('');

    const params = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === 'website_alt') return;
      const v = String(value).trim();
      if (v) params.append(key, v);
    });

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(
          isSpam
            ? '提交未通过校验，请稍后重试'
            : serverMsg && serverMsg.length < 120
              ? serverMsg
              : '提交失败，请稍后重试',
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，请检查网络后重试');
    }
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />
      <main className="flex-1 pt-16">
        {/* 介绍头部 */}
        <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-primary-50 border-b border-primary-100">
          <div className="max-w-4xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium mb-5">
              <i className="ri-music-2-line"></i>
              关于抖音
            </span>
            <h1 className="text-3xl md:text-5xl font-black text-foreground-950 leading-tight mb-4">
              记录美好生活，分享每一个精彩瞬间
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
              抖音是面向年轻人的短视频内容分享与浏览平台。我们相信，每一个日常都值得被记录，
              每一份热爱都值得被看见。从音乐到美食，从搞笑到旅行，在这里你可以快速发现感兴趣的内容，
              也可以把自己的故事分享给更多人。
            </p>
          </div>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((s) => (
              <div key={s.label} className="rounded-lg bg-background-50 border border-primary-100 p-5">
                <p className="text-2xl md:text-3xl font-black text-primary-600">{s.value}</p>
                <p className="text-xs md:text-sm text-foreground-500 mt-1.5">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 平台定位 */}
        <section className="w-full px-4 md:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div>
              <h2 className="text-2xl md:text-3xl font-black text-foreground-950 mb-4">我们的定位</h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                <p>
                  抖音致力于成为年轻人获取优质短视频内容的第一入口。我们通过简洁直观的浏览体验、
                  丰富多元的内容分类，让用户能够高效地搜索、筛选并观看感兴趣的视频。
                </p>
                <p>
                  平台内容覆盖音乐、搞笑、生活、美食、舞蹈、旅行、萌宠、运动等多个热门领域，
                  既有接地气的生活记录，也有让人惊叹的才艺展示，总有一个能戳中你的快乐。
                </p>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3">
                {[
                  { icon: 'ri-rocket-2-line', text: '高效发现内容' },
                  { icon: 'ri-heart-3-line', text: '轻松互动分享' },
                  { icon: 'ri-smartphone-line', text: '移动端友好' },
                  { icon: 'ri-shield-check-line', text: '内容真实呈现' },
                ].map((f) => (
                  <div key={f.text} className="flex items-center gap-2.5 px-4 py-3 rounded-lg bg-background-100/70">
                    <i className={`${f.icon} text-primary-500 text-lg`}></i>
                    <span className="text-sm text-foreground-700">{f.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg bg-accent-50 border border-accent-100 p-6">
              <h2 className="text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-layout-grid-line text-accent-600"></i>
                内容覆盖范围
              </h2>
              <div className="grid grid-cols-2 gap-3">
                {categories.map((c) => (
                  <Link
                    key={c.key}
                    to={`/category/${c.key}`}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg bg-background-50 border border-accent-100 hover:border-accent-300 transition-colors cursor-pointer"
                  >
                    <span className="w-9 h-9 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center shrink-0">
                      <i className={`${c.icon} text-lg`}></i>
                    </span>
                    <span className="text-sm font-medium text-foreground-800">{c.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 联系方式 */}
        <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100/60 border-y border-background-200/70">
          <h2 className="text-2xl md:text-3xl font-black text-foreground-950 mb-2">联系我们</h2>
          <p className="text-sm text-foreground-500 mb-8">点击任意联系方式即可复制，欢迎随时与我们沟通</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {contacts.map((c) => (
              <button
                key={c.label}
                type="button"
                onClick={() => copyText(c.value, c.label)}
                className="group text-left rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 p-5 transition-colors cursor-pointer"
              >
                <span className="w-11 h-11 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center mb-4">
                  <i className={`${c.icon} text-xl`}></i>
                </span>
                <p className="text-xs text-foreground-500 mb-1">{c.label}</p>
                <p className="text-sm font-medium text-foreground-900 break-all">{c.value}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity">
                  <i className="ri-file-copy-line"></i>
                  点击复制
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* 反馈表单 */}
        <section className="w-full px-4 md:px-8 py-12 md:py-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-black text-foreground-950 mb-2 text-center">意见反馈</h2>
            <p className="text-sm text-foreground-500 mb-8 text-center">
              使用中遇到问题，或者有想看的內容类型，都可以告诉我们
            </p>

            {status === 'success' ? (
              <div className="rounded-lg bg-primary-50 border border-primary-100 p-10 text-center">
                <span className="w-14 h-14 rounded-full bg-primary-500 text-background-50 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-2xl"></i>
                </span>
                <h3 className="text-lg font-bold text-foreground-950 mb-2">反馈已收到，谢谢你！</h3>
                <p className="text-sm text-foreground-500 mb-6">我们会认真阅读每一条建议，让平台变得更好</p>
                <button
                  type="button"
                  onClick={() => setStatus('idle')}
                  className="inline-flex items-center gap-2 h-10 px-6 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-edit-line"></i>
                  再写一条
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="feedback-form"
                onSubmit={handleSubmit}
                className="rounded-lg bg-background-50 border border-background-200/70 p-6 md:p-8 space-y-5"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="nickname" className="block text-sm font-medium text-foreground-800 mb-2">
                      你的昵称
                    </label>
                    <input
                      id="nickname"
                      name="nickname"
                      type="text"
                      placeholder="想让我们怎么称呼你"
                      className="w-full h-11 px-4 rounded-md text-sm bg-background-100 text-foreground-900 placeholder:text-foreground-400 outline-none border border-transparent focus:border-primary-300 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-2">
                      联系邮箱
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="方便我们回复你"
                      className="w-full h-11 px-4 rounded-md text-sm bg-background-100 text-foreground-900 placeholder:text-foreground-400 outline-none border border-transparent focus:border-primary-300 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="type" className="block text-sm font-medium text-foreground-800 mb-2">
                    反馈类型
                  </label>
                  <select
                    id="type"
                    name="type"
                    defaultValue="功能建议"
                    className="w-full h-11 px-4 rounded-md text-sm bg-background-100 text-foreground-900 outline-none border border-transparent focus:border-primary-300 focus:ring-2 focus:ring-primary-200 cursor-pointer"
                  >
                    <option value="功能建议">功能建议</option>
                    <option value="内容问题">内容问题</option>
                    <option value="使用体验">使用体验</option>
                    <option value="商务合作">商务合作</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="content" className="block text-sm font-medium text-foreground-800 mb-2">
                    反馈内容
                  </label>
                  <textarea
                    id="content"
                    name="content"
                    rows={4}
                    maxLength={500}
                    placeholder="详细描述你的想法或遇到的问题（最多 500 字）"
                    className="w-full px-4 py-3 rounded-md text-sm bg-background-100 text-foreground-900 placeholder:text-foreground-400 outline-none border border-transparent focus:border-primary-300 focus:ring-2 focus:ring-primary-200 resize-none"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1.5">最多 500 字，我们会认真阅读每一条反馈</p>
                </div>

                <input
                  className="contact-form-verify"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === 'error' && (
                  <div className="flex items-start gap-2 px-4 py-3 rounded-md bg-accent-50 border border-accent-200 text-sm text-accent-900">
                    <i className="ri-error-warning-line mt-0.5"></i>
                    <span>{fieldError || formError}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className={`inline-flex items-center gap-2 h-11 px-8 rounded-full bg-primary-500 text-background-50 text-sm font-semibold transition-colors whitespace-nowrap ${
                    status === 'loading' ? 'opacity-70 cursor-not-allowed' : 'hover:bg-primary-600 cursor-pointer'
                  }`}
                >
                  {status === 'loading' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      正在提交…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      提交反馈
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </section>

        {/* 版权声明 */}
        <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-primary-50 border-t border-primary-100">
          <div className="max-w-4xl">
            <h2 className="text-2xl font-black text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-file-shield-2-line text-primary-500"></i>
              版权与隐私声明
            </h2>
            <div className="space-y-3 text-sm text-foreground-600 leading-relaxed">
              <p>
                本站为短视频内容展示演示平台，页面中的视频、封面、作者信息均为演示数据，
                不代表任何真实内容，播放、点赞与分享功能仅为前端交互演示效果。
              </p>
              <p>
                我们尊重并保护用户隐私。本站不收集、不存储任何个人信息，
                反馈表单仅用于收集建议，不会用于其他用途，也不会与第三方共享。
              </p>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm">
              <Link to="/about" className="text-primary-600 hover:text-primary-700 cursor-pointer flex items-center gap-1">
                <i className="ri-arrow-right-s-line"></i>
                隐私政策
              </Link>
              <Link to="/about" className="text-primary-600 hover:text-primary-700 cursor-pointer flex items-center gap-1">
                <i className="ri-arrow-right-s-line"></i>
                版权声明
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[70] px-5 py-3 rounded-full bg-foreground-950 text-background-50 text-sm animate-fade-up max-w-[90vw] truncate">
          {toast}
        </div>
      )}
    </div>
  );
}